import {
    FetchState,
    HealthPlanType,
    IndividualSignup,
    IndividualEmailVerifyRequest,
    IndividualEmailVerifyResponse,
    IndividualLoginAccountRequest
} from "@/shared/types";

import * as UserService from "@/shared/api/userService";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import _ from "lodash";

export const initialState: IndividualSignup = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    fetchState: FetchState.Uninitialized,
    hsaContributionLimits: {
        familyAmount: 0,
        individualAmount: 0
    },
    partnerId: null,
    isEmployeeSignup: null,
    isEmailVerified: false,
    bendUserId: "",
    signature: "",
    enrollmentReferralCodes: null,
    healthPlanType: HealthPlanType.Unknown,
    hasHsaWithAnotherProvider: null
};

export const useSignUpStore = defineStore("signup", {
    state: (): IndividualSignup => (_.cloneDeep(initialState)),
    getters: {
        individualSignupState: state => ({
            firstName: state.firstName,
            lastName: state.lastName,
            email: state.email,
            phone: state.phone,
            fetchState: state.fetchState,
            hsaContributionLimits: state.hsaContributionLimits,
            partnerId: state.partnerId,
            isEmployeeSignup: state.isEmployeeSignup,
            isEmailVerified: state.isEmailVerified,
            bendUserId: state.bendUserId,
            signature: state.signature,
            enrollmentReferralCode: null,
            healthPlanType: state.healthPlanType,
            hasHsaWithAnotherProvider: state.hasHsaWithAnotherProvider
        }),
        isLoaded: state => state.fetchState === FetchState.Loaded,
        contributionLimits: state => state.hsaContributionLimits,
        hasQuestionnaireData: state => state.healthPlanType !== HealthPlanType.Unknown && state.hasHsaWithAnotherProvider !== null
    },
    actions: {
        setEnrollmentDetails (details: IndividualSignup) {
            if (!details) {
              return;
            }

            this.firstName = details.firstName;
            this.lastName = details.lastName;
            this.email = details.email;
            this.phone = details.phone;
            this.healthPlanType = details.healthPlanType;
            this.hasHsaWithAnotherProvider = details.hasHsaWithAnotherProvider;
        },
        setContributionLimits (value) {
            this.hsaContributionLimits = value;
            this.fetchState = FetchState.Loaded;
        },
        setIndividualSignupEmailVerifiedResponse (response: IndividualEmailVerifyResponse) {
            this.isEmailVerified = true;
            this.bendUserId = response.bendUserId;
            this.signature = response.signature;
        },
        signupIndividual: individualSignup => {
            return UserService.createIndividualSignup(individualSignup)
                .then(response => response.data)
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        resendIndividualSignupEmail: (email): Promise<any> => {
            return UserService.resendIndividualSignupEmail(email)
                .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },
        getContributionLimits () {
            return UserService.getLegalContributionLimitsByPlanType()
                .then(r => {
                    this.setContributionLimits(r.data);
                }).catch(error => storeHelper.handleError({ error, rethrow: true }));
        },
        verifyIndividualSignupEmail (verificationCode: string) {
          const request: IndividualEmailVerifyRequest = {
            email: this.email,
            verificationCode
          };

          return UserService.verifyIndividualSignupEmail(request)
            .then(response => {
              this.setIndividualSignupEmailVerifiedResponse(response.data);
            })
            .catch(error => storeHelper.handleError({ error, rethrow: true }));
        },
        individualSignupCreateLogin: (request: IndividualLoginAccountRequest): Promise<any> => {
            return UserService.individualSignupCreateLogin(request)
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
    }
});
